import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  useTheme,
  alpha,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

interface CountryArrivalData {
  country: string;
  countryCode: string | null;
  totalPassengers: number;
}

interface AirportArrivalsByCountryChartProps {
  data: CountryArrivalData[];
  startYear: number | null;
  startMonth: number | null;
  endYear: number | null;
  endMonth: number | null;
  marketCountry: string | null;
  loading?: boolean;
  title?: string;
}

export const AirportArrivalsByCountryChart: React.FC<AirportArrivalsByCountryChartProps> = ({
  data,
  startYear,
  startMonth,
  endYear,
  endMonth,
  marketCountry,
  loading = false,
  title = 'Last 12 Months Airport Arrivals by Country',
}) => {
  const theme = useTheme();
  const [excludeMarketCountry, setExcludeMarketCountry] = useState(false);

  // Color for bars
  const barColor = '#16d9bc'; // Teal from color scheme (Upper Upscale color)

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const countryData = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            p: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 2,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {countryData.country}
          </Typography>
          {countryData.countryCode && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Code: {countryData.countryCode}
            </Typography>
          )}
          <Typography variant="body2" color="primary">
            Passengers: {payload[0].value.toLocaleString()}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Format the data for the chart
  const chartData = React.useMemo(() => {
    // Filter out market country if checkbox is checked
    let filteredData = data;
    if (excludeMarketCountry && marketCountry) {
      filteredData = data.filter(country => country.country !== marketCountry);
    }

    // Take top 15 countries to keep chart readable
    return filteredData.slice(0, 15).map(country => ({
      ...country,
      displayName: country.countryCode || country.country,
    }));
  }, [data, excludeMarketCountry, marketCountry]);

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton variant="text" width="60%" height={32} />
          <Skeleton variant="rectangular" width="100%" height={350} sx={{ mt: 3 }} />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            {title}
          </Typography>
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="body2" color="text.secondary">
              No arrival data by country available for this market
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {marketCountry && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={excludeMarketCountry}
                  onChange={(e) => setExcludeMarketCountry(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  Exclude {marketCountry}
                </Typography>
              }
              sx={{ m: 0 }}
            />
          )}
        </Box>

        <Box sx={{ width: '100%', height: 350 }}>
          <ResponsiveContainer>
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
              <XAxis
                dataKey="displayName"
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={80}
                stroke={theme.palette.text.secondary}
              />
              <YAxis
                tick={{ fontSize: 12 }}
                stroke={theme.palette.text.secondary}
                tickFormatter={(value) => {
                  if (value >= 1000000) {
                    return `${(value / 1000000).toFixed(1)}M`;
                  } else if (value >= 1000) {
                    return `${(value / 1000).toFixed(0)}K`;
                  }
                  return value.toString();
                }}
                label={{
                  value: 'Arriving Passengers',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 12 },
                }}
              />
              <Tooltip content={<CustomTooltip />} />

              <Bar dataKey="totalPassengers" radius={[8, 8, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={barColor} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Arriving passengers by country of origin (top 15 countries shown)
        </Typography>
        {startYear && startMonth && endYear && endMonth && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Period: {new Date(startYear, startMonth - 1).toLocaleDateString('en-US', {
              month: 'short',
              year: 'numeric'
            })} - {new Date(endYear, endMonth - 1).toLocaleDateString('en-US', {
              month: 'short',
              year: 'numeric'
            })}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
